import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowUpRight } from "lucide-react";

import { authService, formatApiError } from "@/services/authService";
import { useAuth } from "@/contexts/AuthContext";

export default function Dashboard() {
    const { account } = useAuth();
    const [projects, setProjects] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await authService.http.get("/projects");
                setProjects(data);
            } catch (err) {
                setError(formatApiError(err));
            }
        })();
    }, []);

    const count = projects?.length ?? 0;

    return (
        <div data-testid="app-dashboard" className="px-6 lg:px-10 py-10">
            <div className="mx-auto max-w-[1100px]">
                <div className="flex items-baseline justify-between mb-2">
                    <span className="tc-eyebrow text-[#01696f]">Workspace</span>
                    <span className="tc-mono text-[11px] text-[#5a6669]">
                        {count} project{count === 1 ? "" : "s"}
                    </span>
                </div>
                <h1 className="text-[28px] sm:text-[32px] font-medium tracking-tight text-[#0e1416] leading-[1.15]">
                    Welcome back,{" "}
                    <span className="text-[#5a6669]">{account?.name}.</span>
                </h1>
                <p className="mt-3 text-[14px] text-[#5a6669] max-w-[60ch]">
                    Pick up an existing project or start a new one. Concrete Control
                    runs the connected workflow — mix, thermal, maturity, and
                    monitoring — under one project.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                        to="/app/projects/new"
                        data-testid="dashboard-new-project"
                        className="group inline-flex items-center gap-2 bg-[#01696f] hover:bg-[#015a60] text-[#ffffff] px-4 py-2.5 text-[13px] transition-colors"
                    >
                        <Plus size={14} strokeWidth={1.5} />
                        Create project
                    </Link>
                    <Link
                        to="/app/projects"
                        data-testid="dashboard-view-projects"
                        className="inline-flex items-center gap-2 border border-[#d8dcdd] hover:border-[#01696f] hover:text-[#01696f] text-[#0e1416] px-4 py-2.5 text-[13px] transition-colors"
                    >
                        View all projects
                        <ArrowUpRight size={14} strokeWidth={1.5} />
                    </Link>
                </div>

                <section className="mt-12">
                    <div className="flex items-baseline justify-between mb-4">
                        <span className="tc-eyebrow text-[#5a6669]">
                            Recent projects
                        </span>
                        <span className="tc-mono text-[10px] text-[#8a9498]">
                            sorted by created · desc
                        </span>
                    </div>

                    {error && (
                        <div className="border-l-2 border-[#a13434] pl-3 text-[12px] text-[#a13434] tc-mono">
                            {error}
                        </div>
                    )}

                    {projects && projects.length === 0 && (
                        <div
                            data-testid="dashboard-empty-state"
                            className="border border-dashed border-[#d8dcdd] px-6 py-10 text-center"
                        >
                            <div className="tc-mono text-[11px] text-[#5a6669] tracking-widest uppercase mb-2">
                                0 projects
                            </div>
                            <p className="text-[13px] text-[#5a6669] max-w-[44ch] mx-auto leading-[1.55]">
                                Create your first project to access the
                                workflow tabs: Setup, Thermal, Maturity &amp;
                                Strength, Monitoring, Calibration, and Reports.
                            </p>
                        </div>
                    )}

                    {projects && projects.length > 0 && (
                        <ul className="divide-y divide-[#d8dcdd] border-t border-b border-[#d8dcdd]">
                            {projects.slice(0, 6).map((p) => (
                                <li key={p.id}>
                                    <Link
                                        to={`/app/projects/${p.id}`}
                                        data-testid={`dashboard-project-${p.id}`}
                                        className="flex items-center justify-between py-3.5 px-1 hover:bg-[#eef0ef] transition-colors"
                                    >
                                        <div className="flex flex-col">
                                            <span className="text-[14px] text-[#0e1416]">
                                                {p.name}
                                            </span>
                                            <span className="tc-mono text-[10px] text-[#5a6669]">
                                                {p.project_type} · {p.location}
                                            </span>
                                        </div>
                                        <ArrowUpRight
                                            size={14}
                                            strokeWidth={1.5}
                                            className="text-[#5a6669]"
                                        />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
}
